import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../../redux/slices/mainDataSlice";
import toast from "react-hot-toast";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrashAlt,
  faPlus,
  faEdit,
  faCalendarAlt,
  faCheckCircle,
  faCalendarDay,
  faSearch,
  faTimes,
  faUmbrellaBeach,
  faBookOpen,
  faDoorOpen,
  faCalendar,
} from "@fortawesome/free-solid-svg-icons";
import "../../main.scss";
import Can from "../../permissions/Can";
import Tooltip from "../../components/Tooltip";



const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};


const getDayName = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("ar-EG", { weekday: "long" });
};

const getEventCategory = (alhadathText) => {
  const text = alhadathText?.toLowerCase() || "";
  if (text.includes("عطلة")) {
    return {
      key: "holiday",
      label: "عطلة",
      color: "#ef4444",
      bgColor: "rgba(239, 68, 68, 0.1)",
      icon: faUmbrellaBeach,
    };
  }
  if (text.includes("امتحان")) {
    return {
      key: "exam",
      label: "امتحان",
      color: "#f59e0b",
      bgColor: "rgba(245, 158, 11, 0.1)",
      icon: faBookOpen,
    };
  }
  if (text.includes("دوام")) {
    return {
      key: "attendance",
      label: "دوام",
      color: "#10b981",
      bgColor: "rgba(16, 185, 129, 0.1)",
      icon: faDoorOpen,
    };
  }
  return {
    key: "other",
    label: "حدث",
    color: "#6b7280",
    bgColor: "rgba(107, 114, 128, 0.1)",
    icon: faCalendar,
  };
};

const getAcademicTerm = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  if (month >= 9 || month === 1) {
    return "الفصل الدراسي الأول";
  }
  return "الفصل الدراسي الثاني";
};

const isToday = (dateString) => {
  if (!dateString) return false;
  const today = new Date();
  const eventDate = new Date(dateString);
  return (
    today.getDate() === eventDate.getDate() &&
    today.getMonth() === eventDate.getMonth() &&
    today.getFullYear() === eventDate.getFullYear()
  );
};

const SchoolEvents = () => {
  const dispatch = useDispatch();
  const events = useSelector((state) => state.main.data?.eventsData || []);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [animateIn, setAnimateIn] = useState(false);

  const [newEvent, setNewEvent] = useState({
    alhadath: "",
    mn_date: "",
  });
  const [editEvent, setEditEvent] = useState({
    id: null,
    alhadath: "",
    mn_date: "",
  });
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (events.length === 0) {
      dispatch(fetchData()).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [dispatch, events.length]);

  useEffect(() => {
    if (showAddModal || showEditModal) {
      requestAnimationFrame(() => setAnimateIn(true));
    } else {
      setAnimateIn(false);
    }
  }, [showAddModal, showEditModal]);

  const filteredEvents = useMemo(() => {
    return events
      .filter(e => e.alhadath?.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => new Date(a.mn_date) - new Date(b.mn_date)) || [];
  }, [events, searchQuery]);

  const stats = useMemo(() => {
    return {
      total: filteredEvents.length,
      holiday: filteredEvents.filter(e => getEventCategory(e.alhadath).key === "holiday").length,
      exam: filteredEvents.filter(e => getEventCategory(e.alhadath).key === "exam").length,
      attendance: filteredEvents.filter(e => getEventCategory(e.alhadath).key === "attendance").length,
    };
  }, [filteredEvents]);

  const groupedEvents = useMemo(() => {
    return filteredEvents.reduce((acc, event) => {
      const term = getAcademicTerm(event.mn_date);
      if (!acc[term]) acc[term] = [];
      acc[term].push(event);
      return acc;
    }, {});
  }, [filteredEvents]);

  const toggleSelectEvent = (id) => {
    setSelectedEvents((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedEvents.length === 0)
      return toast.error("يرجى اختيار أحداث أولاً");

    toast(
      (t) => (
        <div className="confirm-toast">
          <p>هل أنت متأكد من حذف {selectedEvents.length} حدث؟</p>
          <div className="toast-actions">
            <button
              onClick={async () => {
                const l = toast.loading("جارٍ الحذف...");
                try {
                  const credentials = JSON.parse(
                    localStorage.getItem("userCredentials")
                  );
                  const token = credentials?.token;
                  for (let id of selectedEvents) {
                    await axios.delete(
                      `https://api.alfares.cloud/api/alfares/events/${id}/${token}`
                    );
                  }
                  dispatch(fetchData(true));
                  setSelectedEvents([]);
                  toast.success("تم الحذف بنجاح");
                } catch {
                  toast.error("فشل الحذف");
                } finally {
                  toast.dismiss(l);
                  toast.dismiss(t.id);
                }
              }}
              className="btn-confirm"
            >
              نعم، احذف
            </button>
            <button onClick={() => toast.dismiss(t.id)} className="btn-cancel">
              إلغاء
            </button>
          </div>
        </div>
      ),
      { duration: 6000 }
    );
  };

  const handleEdit = (event) => {
    setEditEvent({
      id: event.id,
      alhadath: event.alhadath,
      mn_date: event.mn_date,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    const credentials = JSON.parse(localStorage.getItem("userCredentials"));
    const token = credentials?.token;

    if (!editEvent.alhadath || !editEvent.mn_date) {
      toast.error("يرجى إدخال اسم الحدث وتاريخه");
      return;
    }

    const loadingToast = toast.loading("جارٍ تحديث الحدث...");
    try {
      await axios.put(
        `https://api.alfares.cloud/api/alfares/events/${editEvent.id}/${token}`,
        {
          alhadath: editEvent.alhadath,
          mn_date: editEvent.mn_date,
        }
      );
      setEditEvent({ id: null, alhadath: "", mn_date: "" });
      setShowEditModal(false);
      toast.success("تم تحديث الحدث بنجاح", { id: loadingToast });
      dispatch(fetchData(true));
    } catch {
      toast.error("فشل في التحديث", { id: loadingToast });
    }
  };

  const handleAdd = async () => {
    const credentials = JSON.parse(localStorage.getItem("userCredentials"));
    const token = credentials?.token;

    if (!newEvent.alhadath || !newEvent.mn_date) {
      toast.error("يرجى إدخال اسم الحدث وتاريخه");
      return;
    }

    const loadingToast = toast.loading("جارٍ إضافة الحدث...");
    try {
      await axios.post(
        `https://api.alfares.cloud/api/alfares/events/${token}`,
        newEvent
      );
      setNewEvent({ alhadath: "", mn_date: "" });
      setShowAddModal(false);
      toast.success("تمت إضافة الحدث بنجاح", { id: loadingToast });
      dispatch(fetchData(true));
    } catch {
      toast.error("فشل في الإضافة", { id: loadingToast });
    }
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditEvent({ id: null, alhadath: "", mn_date: "" });
  };

  return (
    <div className="transferred-wrapper anim-fade-in">
      <div className="premium-edit-container" style={{ maxWidth: "1280px" }}>
        {/* Premium Compact Header */}
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box holiday-icon">
              <FontAwesomeIcon icon={faCalendarDay} />
            </div>
            <div className="header-text-box">
              <h2>أيام الدوام المدرسي</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">إدارة أيام الدوام</span>
              </div>
            </div>
          </div>

          <div className="header-buttons-group">
            <div className="global-search-container">
              <FontAwesomeIcon icon={faSearch} className="s-icon" />
              <input
                type="text"
                placeholder="ابحث عن حدث..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="results-count">
              {filteredEvents.length} حدث
            </div>

            <Can module="holidays" action="delete">
              <button
                className={`bulk-delete-btn ${
                  selectedEvents.length > 0 ? "show" : ""
                }`}
                onClick={handleDeleteSelected}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
                <span>حذف المحدد ({selectedEvents.length})</span>
              </button>
            </Can>

            <Can module="holidays" action="add">
              <button
                onClick={() => setShowAddModal(true)}
                className="premium-action-btn"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>إضافة حدث</span>
              </button>
            </Can>
          </div>
        </div>

        <div className="premium-table-container">
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ جلب السجلات...</span>
            </div>
          ) : filteredEvents.length > 0 ? (
            <>
              {/* Stats Row */}
              <div className="stats-row">
                <div className="stat-card">
                  <span className="stat-label">الإجمالي</span>
                  <span className="stat-value">{stats.total}</span>
                </div>
                <div className="stat-card holiday-stat">
                  <span className="stat-label">عطلة</span>
                  <span className="stat-value">{stats.holiday}</span>
                </div>
                <div className="stat-card exam-stat">
                  <span className="stat-label">امتحان</span>
                  <span className="stat-value">{stats.exam}</span>
                </div>
                <div className="stat-card attendance-stat">
                  <span className="stat-label">دوام</span>
                  <span className="stat-value">{stats.attendance}</span>
                </div>
              </div>

              {/* Grouped Events */}
              {Object.entries(groupedEvents).map(([term, termEvents]) => (
                <div key={term} className="term-group">
                  <div className="term-header">{term}</div>
                  <div className="holidays-modern-grid">
                    {termEvents.map((event) => {
                      const isSelected = selectedEvents.includes(event.id);
                      const isTodayEvent = isToday(event.mn_date);
                      const eventDate = new Date(event.mn_date);
                      const day = eventDate.getDate();
                      const dayName = getDayName(event.mn_date);
                      const category = getEventCategory(event.alhadath);

                      return (
                        <div
                          key={event.id}
                          className={`holiday-card-modern ${
                            isTodayEvent ? "is-today" : ""
                          } ${isSelected ? "is-selected" : ""}`}
                          onClick={() => handleEdit(event)}
                        >
                          {/* Checkbox for selection */}
                          <div
                            className="card-checkbox-wrapper"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelectEvent(event.id);
                            }}
                          >
                            <input type="checkbox" checked={isSelected} readOnly />
                          </div>

                          {/* Calendar Icon Style Date */}
                          <div className="holiday-calendar-icon">
                            <div
                              className="calendar-top"
                              style={{ background: category.color }}
                            >
                              <FontAwesomeIcon icon={category.icon} />
                            </div>
                            <div className="calendar-body">
                              <span className="day-num">{day}</span>
                              <span className="day-name">{dayName}</span>
                            </div>
                          </div>

                          {/* Event Info */}
                          <div className="holiday-content-main">
                            <div className="holiday-title-row">
                              <h3 className="holiday-name">
                                {event.alhadath || "بدون اسم"}
                              </h3>
                              {isTodayEvent && (
                                <span className="today-badge">اليوم</span>
                              )}
                            </div>
                            <div className="holiday-full-date">
                              <FontAwesomeIcon
                                icon={faCalendarAlt}
                                className="date-icon"
                              />
                              <span>{formatDate(event.mn_date)}</span>
                            </div>
                          </div>

                          {/* Quick Actions - Always Visible */}
                          <div className="holiday-card-actions">
                            <Tooltip content="تعديل">
                              <button
                                className="quick-action-btn edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(event);
                                }}
                             
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="empty-ui-state">
              <FontAwesomeIcon icon={faCalendarDay} className="icon" />
              <h3>لا توجد أحداث مطابقة</h3>
              <p>جرّب استخدام كلمات بحث مختلفة أو أضف حدثاً جديداً</p>
            </div>
          )}
        </div>
      </div>

      {/* Premium Modal for Add / Edit */}
      {(showAddModal || showEditModal) && (
        <div className={`premium-modal-overlay ${animateIn ? "active" : ""}`}>
          <div className="premium-modal-card events-modal-card">
            <div className="modal-header events-modal-header">
              <div className="events-modal-header-inner">
                <div className="events-modal-icon-box">
                  <FontAwesomeIcon
                    icon={showAddModal ? faPlus : faEdit}
                    className="events-modal-icon"
                  />
                </div>
                <h3>{showAddModal ? "إضافة حدث جديد" : "تعديل الحدث"}</h3>
              </div>
              <button
                type="button"
                className="events-modal-close-btn"
                onClick={closeModals}
                aria-label="إغلاق"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body events-modal-body">
              {/* اسم الحدث */}
              <div className="events-input-field">
                <label>
                  <span className="req-asterisk">*</span>
                  <span>اسم الحدث</span>
                </label>
                <div className="events-input-wrapper">
                  <FontAwesomeIcon icon={faEdit} className="events-field-icon" />
                  <input
                    type="text"
                    value={
                      showAddModal ? newEvent.alhadath : editEvent.alhadath
                    }
                    onChange={(e) =>
                      showAddModal
                        ? setNewEvent({
                            ...newEvent,
                            alhadath: e.target.value,
                          })
                        : setEditEvent({
                            ...editEvent,
                            alhadath: e.target.value,
                          })
                    }
                    placeholder="مثال: بدء العام الدراسي"
                    className="events-modal-input"
                  />
                </div>
              </div>

              {/* تاريخ الحدث */}
              <div className="events-input-field">
                <label>
                  <span className="req-asterisk">*</span>
                  <span>تاريخ الحدث</span>
                </label>
                <div className="events-input-wrapper">
                  <input
                    type="date"
                    value={showAddModal ? newEvent.mn_date : editEvent.mn_date}
                    onChange={(e) =>
                      showAddModal
                        ? setNewEvent({
                            ...newEvent,
                            mn_date: e.target.value,
                          })
                        : setEditEvent({
                            ...editEvent,
                            mn_date: e.target.value,
                          })
                    }
                    className="events-modal-input events-date-input"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer events-modal-footer">
              <button
                onClick={showAddModal ? handleAdd : handleUpdate}
                className="events-modal-submit"
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>{showAddModal ? "إضافة الحدث" : "تحديث البيانات"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .transferred-wrapper.events-wrapper {
          padding: 5px;
          color: var(--text);
          font-family: 'Cairo', sans-serif;
        }

        .premium-edit-container {
          margin: 0;
          background: transparent;
          border: none;
          border-radius: 0;
          overflow: visible;
          box-shadow: none;
          width: 100%;
          position: relative;
        }

        .premium-header-modern {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-radius: 24px 24px 0 0;
          direction: rtl;
          gap: 20px;
        }
        
        .premium-table-container {
          padding: 25px;
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 24px 24px;
        }

        .stats-row {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 20px;
          direction: rtl;
        }

        .stat-card {
          background: var(--panel-2);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          transition: all 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px -3px rgba(0, 0, 0, 0.2);
        }

        .stat-label {
          font-size: 11px;
          color: var(--muted);
          font-weight: 400;
        }

        .stat-value {
          font-size: 18px;
          font-weight: 400;
          color: var(--text);
        }

        .stat-card.holiday-stat .stat-value { color: #ef4444; }
        .stat-card.exam-stat .stat-value { color: #f59e0b; }
        .stat-card.attendance-stat .stat-value { color: #10b981; }

        .term-group {
          margin-bottom: 25px;
        }

        .term-group:last-child {
          margin-bottom: 0;
        }

        .term-header {
          font-size: 14px;
          font-weight: 400;
          color: var(--muted);
          margin-bottom: 12px;
          padding: 8px 12px;
          background: var(--panel-2);
          border-radius: 8px;
          border-left: 3px solid #10b981;
          direction: rtl;
        }

        .header-left { 
          display: flex; 
          align-items: center; 
          gap: 12px; 
        }
        
        .header-icon-box {
          width: 32px; height: 32px;
          background: linear-gradient(135deg, #10b981, #059669);
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; color: white;
          box-shadow: 0 4px 10px -2px rgba(16, 185, 129, 0.4);
        }
        
        .header-text-box h2 { 
          margin: 0; 
          font-size: 18px; 
          font-weight: 400; 
          color: var(--text); 
          white-space: nowrap;
        }
        .breadcrumb-info { display: flex; align-items: center; gap: 4px; color: var(--muted); font-size: 10px; margin-top: 1px; }
        .current-page { color: #10b981; font-weight: 400; }

        .header-buttons-group {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: nowrap;
        }

        .global-search-container {
          background: var(--panel-2);
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 0 15px;
          display: flex;
          align-items: center;
          gap: 12px;
          width: 280px;
          height: 44px;
          transition: 0.3s;
          box-sizing: border-box;
        }

        .global-search-container input {
          font-size: 12px;
          background: transparent;
          border: none;
          color: var(--text);
          outline: none;
          width: 100%;
          font-family: 'Cairo', sans-serif;
        }

        .results-count {
          background: var(--panel-2);
          padding: 0 15px;
          border-radius: 12px;
          font-size: 13px;
          color: var(--muted);
          border: 1px solid var(--border);
          display: flex;
          align-items: center;
          gap: 6px;
          height: 44px;
          white-space: nowrap;
          transition: all 0.3s;
        }
        .results-count { color: #10b981; }

        .premium-action-btn {
          padding: 6px 14px;
          font-size: 12px;
          font-weight: 400;
          border-radius: 8px;
          gap: 6px;
          display: flex;
          align-items: center;
          cursor: pointer;
          transition: 0.3s;
          border: none;
          font-family: 'Cairo', sans-serif;
          background: #10b981;
          color: white;
          white-space: nowrap;
        }
        .premium-action-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 12px -3px rgba(16, 185, 129, 0.3); }

        .bulk-delete-btn {
          background: #ef4444; color: white; border: none; padding: 6px 14px; border-radius: 8px;
          font-weight: 400; display: none; align-items: center; gap: 6px; font-family: 'Cairo', sans-serif;
          transition: 0.3s; font-size: 12px; cursor: pointer; white-space: nowrap;
        }
        .bulk-delete-btn.show { display: flex; }

        .premium-table-container { padding: 20px; }
        
        .holidays-modern-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 15px;
          direction: rtl;
        }

        .holiday-card-modern {
          background: var(--panel-2);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 15px;
          display: flex;
          align-items: flex-start;
          gap: 15px;
          position: relative;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          overflow: hidden;
        }

        .holiday-card-modern:hover {
          transform: translateY(-3px);
          border-color: #10b981;
          box-shadow: 0 10px 20px -8px rgba(0,0,0,0.4);
          background: var(--panel);
        }

        .holiday-card-modern.is-today {
          border-color: #10b981;
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, transparent 100%);
        }
        .holiday-card-modern.is-today::after {
          content: '';
          position: absolute; top: 0; left: 0; width: 3px; height: 100%; background: #10b981;
        }

        .holiday-card-modern.is-selected {
          border-color: #10b981;
          background: rgba(16, 185, 129, 0.1);
        }

        .card-checkbox-wrapper {
          position: absolute; top: 12px; left: 12px;
          z-index: 5;
        }
        .card-checkbox-wrapper input {
          width: 16px; height: 16px; cursor: pointer;
        }

        .holiday-calendar-icon {
          width: 55px; height: 65px;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 10px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          box-shadow: 0 3px 6px rgba(0,0,0,0.1);
        }

        .calendar-top {
          color: white; font-size: 12px; font-weight: 400;
          text-align: center; padding: 4px 0; text-transform: uppercase;
          display: flex; align-items: center; justify-content: center;
        }
        .calendar-body {
          flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
          background: white; color: #1e293b;
        }
        .day-num { font-size: 20px; font-weight: 400; line-height: 1; }
        .day-name { font-size: 9px; font-weight: 400; margin-top: 1px; opacity: 0.7; }

        .holiday-content-main { flex: 1; }
        .holiday-title-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; flex-wrap: wrap; }
        .holiday-name { margin: 0; font-size: 15px; font-weight: 400; color: var(--text); flex: 1; }
        .today-badge {
          background: #10b981; color: white; font-size: 9px; font-weight: 400;
          padding: 1px 6px; border-radius: 4px;
        }

        .holiday-full-date {
          display: flex; align-items: center; gap: 6px; color: var(--muted); font-size: 12px; font-weight: 400;
        }
        .date-icon { font-size: 12px; color: #10b981; }

        .holiday-card-actions {
          position: absolute; bottom: 12px; left: 12px;
          opacity: 1; transform: translateX(0); transition: 0.3s;
        }

        .quick-action-btn {
          width: 28px; height: 28px; border-radius: 6px; border: none;
          background: var(--panel-2); color: var(--muted); cursor: pointer;
          display: flex; align-items: center; justify-content: center; transition: 0.2s;
          font-size: 12px;
        }
        .quick-action-btn:hover { background: #10b981; color: white; }

        .loader-overlay { padding: 40px; text-align: center; color: var(--muted); display: flex; flex-direction: column; gap: 15px; }
        .premium-loader { width: 32px; height: 32px; border: 3px solid rgba(16, 185, 129, 0.1); border-top-color: #10b981; border-radius: 50%; animation: spin 1s infinite linear; margin: 0 auto; }
        @keyframes spin { to { transform: rotate(360deg); } }

        .empty-ui-state { text-align: center; padding: 60px 20px !important; }
        .empty-ui-state .icon { font-size: 50px; color: #10b981; opacity: 0.2; margin-bottom: 20px; }
        .empty-ui-state h3 { font-size: 18px; font-weight: 400; color: var(--text); margin-bottom: 8px; }
        .empty-ui-state p { font-size: 14px; color: var(--muted); }

        .premium-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(13, 35, 32, 0.4); backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center; z-index: 10000;
          opacity: 0; pointer-events: none; transition: 0.3s;
        }
        .premium-modal-overlay.active { opacity: 1; pointer-events: auto; }
        .premium-modal-overlay.active .premium-modal-card { transform: translateY(0) scale(1); }

        .events-modal-card {
          background: #FFFFFF;
          border: 1px solid #e0e2e6;
          width: 100%;
          max-width: 380px;
          border-radius: 16px;
          padding: 0;
          overflow: hidden;
          transform: translateY(20px) scale(0.97);
          transition: 0.3s;
          box-shadow: 0 24px 60px -16px rgba(0,0,0,0.35);
          font-family: 'Cairo', sans-serif;
          direction: rtl;
        }

        .events-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 24px;
          background: #FFFFFF;
          border-bottom: 1px solid #f1f5f9;
        }
        .events-modal-header-inner {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .events-modal-icon-box {
          width: 32px;
          height: 32px;
          background: rgba(16, 185, 129, 0.1);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .events-modal-icon {
          font-size: 16px;
          color: #10b981;
        }
        .events-modal-header h3 {
          margin: 0;
          font-size: 15px;
          font-weight: 400;
          color: #181d26;
          line-height: 1.4;
        }
        .events-modal-close-btn {
          width: auto;
          min-width: 0;
          padding: 6px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #6b7280;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: 0.2s;
        }
        .events-modal-close-btn:hover {
          background: #f1f5f9;
          color: #181d26;
        }

        .events-modal-body {
          padding: 20px 24px;
          background: #F8FAFC;
        }

        .events-input-field {
          margin-bottom: 16px;
        }
        .events-input-field:last-child {
          margin-bottom: 0;
        }
        .events-input-field label {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          font-weight: 400;
          color: #4B5563;
          margin-bottom: 8px;
          line-height: 1.4;
        }
        .req-asterisk {
          color: #ef4444;
          font-size: 12px;
          line-height: 1;
        }
        .events-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .events-field-icon {
          position: absolute;
          right: 12px;
          color: #10b981;
          font-size: 14px;
          z-index: 1;
        }
        .events-modal-input {
          width: 100%;
          height: 40px;
          background: #FFFFFF;
          border: 1px solid #e0e2e6;
          border-radius: 8px;
          padding: 0 14px 0 12px;
          color: #181d26;
          font-family: 'Cairo', sans-serif;
          font-size: 13px;
          font-weight: 400;
          transition: 0.2s;
          outline: none;
          box-sizing: border-box;
          direction: rtl;
        }
        .events-modal-input::placeholder {
          color: #9ca3af;
          font-weight: 400;
        }
        .events-input-wrapper:not(:has(.events-date-input)) .events-modal-input {
          padding: 0 38px 0 12px;
        }
        .events-date-input {
          font-family: Arial, sans-serif;
          direction: ltr;
          text-align: center;
          padding: 0 10px;
        }
        .events-modal-input:focus {
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
          background: #FFFFFF;
        }

        .events-modal-footer {
          padding: 12px 24px;
          background: #FFFFFF;
          border-top: 1px solid #f1f5f9;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }
        .events-modal-submit {
          min-width: 120px;
          background: #10b981;
          border: none;
          color: white;
          height: 38px;
          padding: 0 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 400;
          cursor: pointer;
          transition: 0.2s;
          font-family: 'Cairo', sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          white-space: nowrap;
        }
        .events-modal-submit:hover {
          background: #059669;
          transform: translateY(-1px);
        }

        @media (max-width: 1000px) {
          .header-tools, .header-buttons-group { flex-direction: column; width: 100%; gap: 10px; }
          .global-search-container, .results-count { display: none !important; }
          .stats-row { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 600px) {
          .stats-row { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default SchoolEvents;
